/**
 * Dutch message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal je (locked in l10n/locales.md).
 *
 * Platform terms follow Dutch UI conventions (Thema for theme, gesprek
 * for conversation, koppelen for pair, wissen for dismiss, ontvanger
 * for a receiving surface); "Glance" is the untranslated feature brand
 * name (glossary D1) and "clip"/"clips" is a locked loanword (D2).
 *
 * Plural notes: Dutch is a {one, other} CLDR locale, and unlike German
 * its count nouns do inflect (teken → tekens, resultaat → resultaten),
 * so every count-bearing function selects through `selectPlural("nl", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Verbonden",
    statusReconnecting: "Opnieuw verbinden…",
    statusDisconnected: "Verbroken",
    openSettings: "Instellingen openen",
    pairedFallbackDevice: "je telefoon",
    publicModeToggleEnable: "Meldingsinhoud verbergen",
    publicModeToggleDisable: "Meldingsinhoud tonen",
  },

  notifications: {
    replyAriaLabel: "Antwoorden",
    replyPlaceholder: "Antwoorden…",
    replySend: "Verzenden",
    sent: "Verzonden",
    failedToSend: "Verzenden mislukt",
    unread: "Ongelezen",
    dismiss: "Wissen",
    notificationImageAlt: "Meldingsafbeelding",
  },

  conversations: {
    replyAriaLabel: "Antwoorden",
    replyPlaceholder: "Antwoorden…",
    replySend: "Verzenden",
    sent: "Verzonden",
    failedToSend: "Verzenden mislukt",
    dismiss: "Gesprek wissen",
    messagesListLabel: "Berichten in dit gesprek",
    deviceOwner: "Jij",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Geen ongelezen berichten"
        : `${count} ${selectPlural("nl", count, {
            one: "ongelezen bericht",
            other: "ongelezen berichten",
          })}`,
  },

  list: {
    today: "Vandaag",
    yesterday: "Gisteren",
    searchNotifications: "Meldingen zoeken",
    clearSearch: "Zoekopdracht wissen",
    clearAll: "Alles wissen",
    markAllRead: "Alles als gelezen markeren",
    groupCheckboxLabel: "Groeperen",
    noNotificationsTitle: "Nog geen meldingen",
    noNotificationsDesc:
      "Nieuwe meldingen die vanaf je telefoon worden gespiegeld, verschijnen hier.",
    noMatchesTitle: "Geen resultaten",
    notificationsList: "Meldingen",
    privacyAppList: "Meldingen per app",
    clear: "Wissen",
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("nl", count, { one: "resultaat", other: "resultaten" })} wissen`,
    noMatchesDesc: (query: string): string => `Geen meldingen komen overeen met „${query}”.`,
    appNotificationsGroup: (appName: string): string => `Meldingen van ${appName}`,
    dismissAllFrom: (appName: string): string => `Alles van ${appName} wissen`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("nl", count, { one: "bericht", other: "berichten" })
          : kind === "calls"
            ? selectPlural("nl", count, { one: "oproep", other: "oproepen" })
            : selectPlural("nl", count, { one: "melding", other: "meldingen" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "wissen",
    kbdNavigate: "navigeren",
    kbdOrSeparator: "of",
  },

  settings: {
    back: "Terug naar meldingen",
    settings: "Instellingen",
    general: "Algemeen",
    theme: "Thema",
    themeAuto: "Automatisch",
    themeLight: "Licht",
    themeDark: "Donker",
    language: "Taal",
    languageSystemDefault: "Systeemstandaard",
    notificationsSection: "Meldingen",
    osNotifications: "Systeemmeldingen",
    callNotifications: "Oproepmeldingen",
    privacySecurity: "Privacy",
    analyticsToggle: "Anonieme gebruiksstatistieken",
    analyticsToggleDescription: "Alleen aantallen en mijlpalen — nooit je meldingen.",
    statusConnected: "Verbonden",
    statusReconnecting: "Opnieuw verbinden…",
    statusDisconnected: "Verbroken",
    disconnectFromPhone: "Verbinding met telefoon verbreken",
    disconnectConfirm: "Weet je het zeker? Hiermee wordt je apparaat ontkoppeld.",
    yesDisconnect: "Ja, verbreken",
    cancel: "Annuleren",
    version: "Versie",
    encryption: "Versleuteling",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Hulp nodig? Mail __EMAIL__",
    disconnectFailed: "Verbinding verbreken mislukt — probeer het opnieuw",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Verbinding verbroken, maar het lokaal opschonen is mislukt: ${errorMsg}. Laad de pagina opnieuw.`,
    disconnectLocalCleanupFailed:
      "Verbinding verbroken, maar het lokaal opschonen is mislukt. Laad de pagina opnieuw.",
  },

  pairing: {
    enterCodeInstruction: "Voer deze code in de Reflecto-app in",
    pairedSuccessfully: "Koppeling geslaagd!",
    pairingFailed: "Koppeling mislukt",
    requestingPairingCode: "Koppelingscode aanvragen…",
    codeExpired: "Code verlopen",
    tryAgain: "Opnieuw proberen",
    failedToStartPairing: "Kon koppeling niet starten",
    installHeading: "Heb je de app nog niet?",
    installScanHint: "Scan met de camera van je telefoon",
    installQrLabel: "QR-code om Reflecto op je telefoon te installeren",
    capabilitiesHeading: "Na het koppelen kun je:",
    capabilityMirror: "De meldingen van je telefoon hier zien",
    capabilityReply: "Antwoorden zonder je telefoon te pakken",
    capabilityShare: "Klembord en bestanden in beide richtingen delen",
    encryptionNote: "End-to-end versleuteld. De server ziet je inhoud nooit.",
  },

  dnd: {
    title: "Niet storen",
    description: "Pauzeer meldingen op dit apparaat voor het komende uur.",
    snooze1h: "1 uur pauzeren",
    snoozeActiveUntil: (formattedTime: string): string => `Actief tot ${formattedTime}`,
    cancelSnooze: "Annuleren",
  },

  connectors: {
    title: "Verbonden diensten",
    emptyBody:
      "Nog geen verbonden diensten. Verbind ChatGPT of Claude via hun connectorinstellingen en bevestig daarna de code op je telefoon.",
    scopeSend: "Kan meldingen sturen",
    added: (date: string): string => `Toegevoegd ${date}`,
    lastUsed: (relative: string): string => `Laatst gebruikt ${relative}`,
    neverUsed: "Nooit gebruikt",
    revoke: "Verbreken",
    revokeConfirm: (label: string): string =>
      `${label} verbreken? De toegang eindigt onmiddellijk.`,
    revokeAction: "Verbreken",
    revokeConfirmLabel: (label: string): string => `${label} verbreken`,
    cancel: "Annuleren",
    loadError: "Kon verbonden diensten niet laden. Probeer het opnieuw.",
    revokeError: "Kon de dienst niet verbreken. Probeer het opnieuw.",
    retry: "Opnieuw",
  },

  safetyNumber: {
    rowLabel: "Versleuteling controleren",
    rowSublabel: "Beveiligingsnummers met je telefoon vergelijken",
    cardTitle: "Beveiligingsnummer",
    caption: (deviceLabel: string): string =>
      `Vergelijk met het nummer dat op ${deviceLabel} wordt weergegeven. Als beide overeenkomen, is je end-to-end-versleuteling intact.`,
    repairNote: "Dit nummer verandert wanneer je een apparaat opnieuw koppelt.",
    copy: "Kopiëren",
    copied: "Gekopieerd",
    maskedHint: "Verborgen terwijl privacymodus aan is",
  },

  feedback: {
    title: "Feedback verzenden",
    close: "Sluiten",
    messageLabel: "Je bericht",
    messagePlaceholder:
      "Een bug gevonden, een idee, of voelt er iets niet goed? Laat het ons weten.",
    categoryLabel: "Categorie",
    categoryBug: "Bug",
    categoryIdea: "Idee",
    categoryOther: "Overig",
    emailLabel: "E-mail (optioneel, alleen als je een antwoord wilt)",
    emailPlaceholder: "jij@voorbeeld.nl",
    emailHelp: "We zetten je op geen enkele lijst.",
    disclosure:
      "Je bericht wordt naar de server van Reflecto verzonden en vastgelegd in onze interne issue-tracker. We voegen je app-versie en een bij de koppeling toegewezen apparaat-ID toe zodat we rapporten kunnen correleren. We zien je meldingen, contacten of andere persoonlijke gegevens niet. Je e-mailadres is optioneel — geef het alleen op als je een antwoord wilt. We zetten je op geen enkele lijst.",
    submit: "Feedback verzenden",
    sending: "Verzenden…",
    success: "Bedankt — wordt door een mens gelezen, meestal binnen 24 uur",
    error: "Verzenden mislukt. Probeer het opnieuw.",
    done: "Klaar",
    sendFeedback: "Feedback verzenden",
  },

  uninstall: {
    headline: "De Reflecto-extensie is verwijderd",
    body: "Er was nooit een account, en de extensie bewaarde je koppeling op deze computer, dus er blijft niets achter om op te ruimen.",
    ask: "Als je dertig seconden hebt, horen we graag wat er misging.",
    categoryLegend: "Wat voor feedback is dit?",
    categoryBug: "Er ging iets kapot",
    categoryIdea: "Er ontbrak iets dat ik nodig had",
    categoryOther: "Iets anders",
    messageLabel: "Wat ging er mis?",
    messagePlaceholder: "Na een paar uur kwamen er geen meldingen meer binnen.",
    messageRequired: "Schrijf eerst een zin en verstuur dan.",
    charactersLeft: (count: number): string => `Nog ${count} tekens`,
    emailLabel: "E-mail (optioneel)",
    emailPlaceholder: "jij@voorbeeld.nl",
    emailHelp: "Alleen gebruikt om je te antwoorden. Verder niets.",
    submit: "Feedback verzenden",
    sending: "Verzenden…",
    retry: "Opnieuw proberen",
    privacyNote:
      "Dit formulier is aan geen enkel apparaat gekoppeld. Het bevat geen van je meldingen, je klembord of je bestanden. E-mail is optioneel en wordt alleen gebruikt om te antwoorden.",
    success: "Bedankt. Een mens leest ze allemaal.",
    errorGeneric: "Dit is niet verzonden. Je bericht staat er nog.",
    errorRateLimited:
      "Je hebt onlangs meerdere berichten verstuurd. Probeer het over ongeveer een uur opnieuw. Je tekst blijft staan.",
    footerWebApp: "De Reflecto-web-app werkt ook zonder de extensie.",
    footerWebAppLink: "Web-app openen",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Sluiten",
    aiSummaryTag: "AI-samenvatting",
    errorAi: "Kon nu geen samenvatting maken.",
    retryCta: "Opnieuw proberen",
    downloadHeading: "AI-samenvattingen krijgen",
    downloadBody:
      "Kom bij met een AI-overzicht op het apparaat zelf van wat je hebt gemist. Draait volledig op je apparaat.",
    downloadCta: "Model downloaden",
    downloadPrivacyNote: "Eenmalige download. De server ziet je meldingen nooit.",
    downloadingHeading: "Model downloaden…",
    caughtUp: "Je bent helemaal bij — er is nu niets dat je aandacht vraagt.",
    needsYou: "Vereist aandacht",
    quiet: "Overig",
    scanning: (pending: number): string => `Samenvatten van ${pending}…`,
    jump: "Melding openen",
    affordanceReply: "Antwoorden",
    affordanceOpen: "Openen",
    affordanceCopyOtp: "Code kopiëren",
    buttonLabel: "Glance",
  },

  update: {
    title: "Update beschikbaar",
    description: "Er is een nieuwe versie van Reflecto klaar. Laad opnieuw om bij te werken.",
    reload: "Opnieuw laden",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `We hebben dit apparaat „${name}” genoemd.`,
    cardLooksGood: "Prima",
    cardEdit: "Bewerken",
    cardAriaLabel: "Apparaatnaam bevestigen",
    renameTitle: "Dit apparaat hernoemen",
    renameLabel: "Apparaatnaam",
    renameSave: "Opslaan",
    renameCancel: "Annuleren",
    renameFailed: "Hernoemen mislukt. Probeer het opnieuw.",
  },

  collision: {
    title: (name: string): string => `Je hebt al een „${name}”`,
    subtitle: "Hoe zullen we deze noemen?",
    chipWork: (name: string): string => `Werk ${name}`,
    chipHome: (name: string): string => `Thuis ${name}`,
    chipPersonal: (name: string): string => `Privé ${name}`,
    customLabel: "Aangepaste naam",
    save: "Opslaan",
    useAnyway: "Toch de oorspronkelijke naam gebruiken",
    saveFailed: "Kon de nieuwe naam niet opslaan. Probeer het opnieuw.",
    ariaLabel: "Dubbele apparaatnaam oplossen",
  },

  clipboard: {
    title: "Klembord",
    emptyTitle: "Nog geen clips",
    empty: "Clips van je andere apparaten verschijnen hier. Tik om te kopiëren.",
    from: (label: string): string => `van ${label}`,
    to: (label: string): string => `naar ${label}`,
    copy: "Naar klembord kopiëren",
    copied: "Gekopieerd",
    reveal: "Tonen",
    hide: "Verbergen",
    maskedHint: "Inhoud verborgen — druk op Tonen om weer te geven",
    sensitiveChip: "Gevoelig",
    cloudChip: "via je API-token",
    delete: "Clip verwijderen",
    sendPlaceholder: "Plak tekst om te verzenden…",
    sendCta: "Verzenden",
    sendSensitiveToggle: "Als gevoelig markeren",
    sendSuccess: (n: number): string =>
      `Verzonden naar ${n} ${selectPlural("nl", n, { one: "apparaat", other: "apparaten" })}.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("nl", total, { one: "apparaat", other: "apparaten" })} bereikt.`,
    sendNoDevices: "Geen gekoppelde apparaten om naar te verzenden.",
    sendFailed: "Verzenden mislukt. Probeer het opnieuw.",
    sendEmpty: "Niets te verzenden.",
    dockRecentHeading: "Recent",
    dockEmptyClipboard: "Je klembord is leeg.",
    clearAll: "Alles wissen",
    clearAllConfirm: "Ja, alles wissen",
    clearAllCancel: "Annuleren",
    shareTitle: "Naar je apparaten verzenden",
    shareReview:
      "Controleer wat je verzendt. Het gaat end-to-end-versleuteld naar je gekoppelde apparaten.",
    shareSend: "Verzenden",
    shareCancel: "Annuleren",
    shareNotPaired: "Koppel eerst een apparaat om gedeelde tekst te verzenden.",
    shareOpenApp: "Reflecto openen",
    pasteFromClipboard: "Plakken vanaf klembord",
    readFailed: "Kan het klembord niet lezen — plak hieronder.",
  },
  install: {
    title: "Reflecto installeren",
    body: "Voeg toe aan je startscherm voor snellere toegang.",
    cta: "Installeren",
    dismissAria: "Installatieprompt sluiten",
  },
  nav: {
    home: "Start",
    clipboard: "Klembord",
    files: "Bestanden",
    ariaLabel: "Hoofdnavigatie",
    fileHiddenPublicMode: "Verborgen zolang de privémodus aanstaat.",
  },
  common: {
    loading: "Laden",
  },
  errors: {
    notPaired: "Niet gekoppeld",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} op ${os}`,
    webFallback: "Webbrowser",
  },

  palette: {
    placeholder: "Typ een opdracht…",
    noResults: "Geen overeenkomende opdrachten",
    ariaLabel: "Opdrachtenpalet",
    clearAll: "Alle meldingen wissen",
    glance: "Glance-samenvatting",
    cancelSnooze: "Pauze annuleren",
    copyClip: "Laatst ontvangen clip kopiëren",
  },

  fileTransfer: {
    sendTitle: "Een bestand verzenden",
    sendChooseFile: "Bestand kiezen",
    sendDeviceLabel: "Verzenden naar",
    sendPhoneFallbackName: "Telefoon",
    send: "Bestand verzenden",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Voorbereiden…" : `Voorbereiden — ${pct}%`,
    sendDelivering: "Bezorgen op je telefoon…",
    sendWaitingAccept: "Wachten tot je telefoon accepteert…",
    sendWaitingAppOpen: "Open Reflecto op je telefoon om te accepteren",
    sendWaitingAppOpenHint:
      "Je telefoon kan geen pushmeldingen ontvangen en toont dit bestand pas als Reflecto open is.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Deel ${partNumber} van ${partCount} uploaden…`,
    sendDone: "Verzonden.",
    sendFailedRejected: "Je telefoon heeft het bestand geweigerd.",
    sendFailedNoAnswer: "Geen antwoord van je telefoon.",
    sendFailedUploadFailed: "Uploaden mislukt. Probeer het opnieuw.",
    sendFailedCancelled: "Geannuleerd.",
    sendFailedGeneric: "Er is iets misgegaan. Probeer het opnieuw.",
    sendTooLarge: (maxMiB: number): string => `Het bestand is te groot (max. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Houd dit tabblad open totdat de overdracht is voltooid.",
    sendAnother: "Nog een verzenden",
    sendNoDevice: "Koppel eerst een telefoon om bestanden te verzenden.",
    sendCancel: "Annuleren",
    sendPeerProgress: (chunksDone: number): string =>
      `Je telefoon heeft ${chunksDone} blokken ontvangen.`,

    receiveTitle: "Inkomende bestanden",
    receiveOfferFrom: (sender: string): string => `${sender} wil je een bestand sturen`,
    receiveAccept: "Accepteren",
    receiveDecline: "Weigeren",
    receiveReceiving: (n: number, total: number): string => `Deel ${n} van ${total} ontvangen…`,
    receiveDone: "Opgeslagen.",
    receiveFailed: "Overdracht mislukt.",
    receiveCancelled: "Geannuleerd.",
    receiveCancel: "Annuleren",

    errorChunkAuthFail:
      "Overdracht mislukt — de gegevens konden niet worden geverifieerd. Probeer het opnieuw.",
    errorHashMismatch:
      "Overdracht mislukt — het bestand kwam niet door de integriteitscontrole. Probeer het opnieuw.",
    errorStorageFull: "Niet genoeg opslagruimte voor deze overdracht.",
    errorExpired: "Overdracht verlopen.",
    errorQuotaExceeded: "Dagelijkse overdrachtslimiet bereikt — probeer het morgen opnieuw.",
    errorInternal: "Overdracht mislukt — er is iets misgegaan.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Sluiten",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Voortgang van overdracht",
    outgoing: (n: number): string => `Uitgaand (${n})`,
    incoming: (n: number): string => `Inkomend (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Alles accepteren",
    dropTitle: "Sleep hierheen",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Bestanden tot ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Sleep hierheen of kies een bestand om te versturen",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direct",
    transportDirectHint:
      "Rechtstreeks tussen apparaten verstuurd — nooit op onze servers opgeslagen.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via server",
    transportRelayHint:
      "Doorgestuurd via onze servers, nog steeds end-to-end versleuteld — wij kunnen het niet lezen.",
    directBadge: "Direct",
  },
  time: {
    justNow: "zojuist",
    minutesAgo: (count: number): string => `${count} min. geleden`,
    hoursAgo: (count: number): string => `${count} u geleden`,
  },
} satisfies MessagesShape;
